import { DeckColumnChangeSetSchema } from '@schema/DeckColumn.schema';
import { PageChangeSetSchema } from '@schema/Page.schema';
import { z } from 'zod';

import { AuditHistoryChangeSetSchema } from './AuditHistory.schema';
import { ChangeSetTargets } from './Common.schema';
import { DecklineChangeSetSchema } from './Deckline.schema';
import { FootnoteChangeSetSchema } from './Footnote.schema';
import { ScenarioChangeSetSchema } from './ScenarioManagement.schema';
import { TOCChangeSetSchema } from './TOC.schema';

/**
 * SaveDeckChangeSetSchema
 *
 * This schema validates the complete change_set structure for SaveDeckInput.
 * Each target is optional, allowing for partial updates (which is a typical workflow).
 */
export const SaveDeckChangeSetSchema = z
  .object({
    [ChangeSetTargets.audit]: AuditHistoryChangeSetSchema.optional(),
    [ChangeSetTargets.scenario]: ScenarioChangeSetSchema.optional(),
    [ChangeSetTargets.toc]: TOCChangeSetSchema.optional(),
    [ChangeSetTargets.deckcolumn]: DeckColumnChangeSetSchema.optional(),
    [ChangeSetTargets.deckline]: DecklineChangeSetSchema.optional(),
    [ChangeSetTargets.page]: PageChangeSetSchema.optional(),
    // TODO: Future targets can be added here:
    // [ChangeSetTargets.deck]: DeckChangeSetSchema.optional(),
    [ChangeSetTargets.footnote]: FootnoteChangeSetSchema.optional(),
  })
  .strict(); // Reject unknown keys

/**
 * Type for the complete SaveDeck changeset structure
 * This will have full TypeScript inference for all nested properties
 */
export type SaveDeckChangeSetType = z.infer<typeof SaveDeckChangeSetSchema>;

/**
 * Example usage:
 *
 * const changeSet: SaveDeckChangeSetType = {
 *   scenario: {
 *     create: [{ deck_cycle: "Q1-2024", ... }],
 *     update: [{ pk: "deck#456", sk: "scenario#123", ... }]
 *   },
 *   toc: {
 *     delete: [{ pk: "deck#456", sk: "toc#789" }]
 *   }
 * };
 *
 * const validated = SaveDeckChangeSetSchema.parse(changeSet);
 */
